import { Spiner } from "../../../components/spinner/Spiner";
import { useEffect, useState } from "react";
import { auth } from "../../../firebase";
import Button from "../../../components/button/Button";
import { useLocation, useNavigate } from "react-router-dom";
import { EventParticipationWebSearch } from "./EventParticipationWebSearch";
import companiesQuery from "../../../firestore/Companies.query";
import { convertDateTimeToJapaneseStringWithWeekZero } from "../../../utility/ConvertUtily";
import { EventParticipationAttendanceHistorySortParam, EventParticipationHistory, EventParticipationTimestampToDate } from "../../../model/EventParticipationHistory";
import messagesQuery from "../../../firestore/Messages.query";

export const EventParticipationWeb = () => {

  const navigate = useNavigate();

  // ログイン中のユーザー情報
  const currentUser = auth.currentUser;

  const [isLoading, setIsLoading] = useState(true);
  const [eventParticipationHistoryList, setEventParticipationHistoryList] = useState<EventParticipationHistory[]>([]);
  const [historySortParam, setHistorySortParam] = useState<EventParticipationAttendanceHistorySortParam>({ title: null, eventAt: 'desc' });
  const [sortedHistories, setSortedHistories] = useState<EventParticipationHistory[]>([]);
  const [displayLimit, setDisplayLimit] = useState(50);

  const location = useLocation();

  useEffect(() => {
    const windowBreakPoint = 1024;
    if (window.innerWidth < windowBreakPoint) {
      // 画面幅が1024px未満の場合
      navigate("/portal/eventParticipationMobile");
    }
  },[location.pathname, navigate]);

  // 初回取得
  useEffect(() => {
    (async () => {
      // 団体を取得
      const company = await companiesQuery.getCompany(currentUser?.tenantId!);
      const histories = await messagesQuery.getEventParticipationAttendanceHistory(company.id, currentUser?.displayName!, currentUser?.uid!);
      const converted = histories.map(EventParticipationTimestampToDate);
      setEventParticipationHistoryList(converted);
      setIsLoading(false);
    })();
  },[]);

  // 並び替え処理
  useEffect(() => {
    let sorted = [...eventParticipationHistoryList];
    if (historySortParam.eventAt) {
      sorted.sort((a, b) =>
        historySortParam.eventAt === 'asc'
          ? new Date(a.eventAt).getTime() - new Date(b.eventAt).getTime()
          : new Date(b.eventAt).getTime() - new Date(a.eventAt).getTime()
      );
    } else if (historySortParam.title) {
      sorted.sort((a, b) =>
        historySortParam.title === 'asc'
          ? (a.title ?? '').localeCompare(b.title ?? '')
          : (b.title ?? '').localeCompare(a.title ?? '')
      );
    }
    setSortedHistories(sorted);
  }, [eventParticipationHistoryList, historySortParam]);

  // 履歴を追加取得
  const onClickMore = () => {
    setDisplayLimit((prev) => prev + 50);
  };

  const rowList = sortedHistories.slice(0, displayLimit).map((history) => ({
    title: history.title,
    eventAt: convertDateTimeToJapaneseStringWithWeekZero(new Date(history.eventAt)),
    attendanceButtonName: history.attendanceButtonName,
    menu: (
      <Button
        onClick={() => navigate(`/portal/eventParticipationWeb/${history.messageId}/info`)}
        className={"h-10 text-sm items-center whitespace-pre w-[55px] h-[50px]"}
        color={"darkOrange"}
        children="読む" />
    )
  }));

  return (
    <div className="h-full mx-2 min-w-max">
      <div className="flex items-center p-4 border-b">
        <h2 className="text-2xl font-semibold text-gray-900">
          イベント参加履歴
        </h2>
      </div>
      <div className={'p-4 border-b'}>
        <EventParticipationWebSearch
          stateHistorySortParam={historySortParam}
          stateSetHistorySortParam={setHistorySortParam}
        />
      </div>
      <div className={'px-4 pb-2'}>
        {rowList.map((item, i) => (
          <div key={i} className="flex justify-between border-b py-2">
            <div className="flex flex-col w-full">
              <div className="flex">
                <span className="w-1/3">
                  <div className="flex justify-center items-center relative text-orange-400 border-orange-400 border-[3px] border-solid border-r-0 font-bold h-8 w-fit px-2 md:px-4">
                    <span className="absolute border-orange-400 border-t-[3px] border-r-[3px] right-[-0.7rem] h-[1.4rem] w-[1.4rem] transform rotate-45" />
                    {item.attendanceButtonName}
                  </div>
                </span>
                <span className="w-2/3">{item.eventAt}</span>
              </div>
              <p className="text-xl font-semibold">{item.title}</p>
            </div>
            <div className="flex items-center">
              {item.menu}
            </div>
          </div>
        ))}
        <div className={"my-3 flex justify-center"}>
          {isLoading ? (
            <div className={"py-1"}><Spiner /></div>
          ) : (
            sortedHistories.length > displayLimit && (
              <Button
                onClick={onClickMore}
                className={"h-12 w-1/4 items-center"}
                color={"darkOrange"}
                children="もっと見る" />
            )
          )}
        </div>
      </div>
    </div>
  );
};